import { useState } from 'react';
import { Box } from '@mui/material';
import { Button } from '@components/button';
import { useParams } from 'react-router-dom';
import { Table } from '@components/table/Table';
import { useSearchUnderwritingActivity } from '@services/underwritingReferral/useSearchUnderwritingActivity';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { getUnderwritingActivityColumns } from './getUnderwritingActivityColumns';
import invariant from 'tiny-invariant';
import { EditUWActivityDialog } from '@pages/UnderwriterToolkit/tab-content/UnderwritingActivityPanel/EditUWActivityDialog';
import { UnderwritingActivityItem } from '@models/underwritingReferral';
import { Plus } from '@assets/svgs';
import { underwritingTabStyles } from '@pages/UnderwriterToolkit/tab-content/UnderwritingTab.styles';
import { quoteStatusTerminal } from '@models/quotes';
import { useQuote } from '@services/quotes';

export const UnderwritingActivityPanel = () => {
  const { quoteId } = useParams<'quoteId'>();
  invariant(quoteId, 'quoteId is required');
  const { quote } = useQuote(quoteId);
  const { underwritingActivity, isLoading } = useSearchUnderwritingActivity({
    quoteId,
    pageLimit: 100,
  });

  const [editOpen, setEditOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<UnderwritingActivityItem | null>(null);

  const [layoutPreferences] = useStore((state) => [state.layoutPreferences], shallow);
  const clockFormat = layoutPreferences.clockFormat;

  const handleEditClick = (activity: UnderwritingActivityItem) => {
    setSelectedActivity(activity);

    setEditOpen(true);
  };

  const handleRowClick = (row: UnderwritingActivityItem) => {
    setSelectedActivity(row);

    setEditOpen(true);
  };

  const columns = getUnderwritingActivityColumns(handleEditClick, clockFormat);
  const allQuoteUnderwritingActivity = underwritingActivity?.pages?.flatMap((page) => page.items);

  const isTerminalStatus = !!quote?.status.name && quoteStatusTerminal.includes(quote?.status.name);

  const isAddDisabled = isTerminalStatus;
  return (
    <Box sx={underwritingTabStyles.activityPanel}>
      <Box sx={underwritingTabStyles.tableWrapper}>
        <Table
          ariaLabel='Underwriting Activity Table'
          columns={columns}
          onLoadData={() => ({
            data: { items: allQuoteUnderwritingActivity || [] },
            isLoading,
            isFetching: false,
            isError: false,
          })}
          rowConfig={{
            getRowHeight: () => 50,
            onRowClick: ({ row }) => handleRowClick(row),
          }}
          sxProps={{ ...underwritingTabStyles }}
          hidePagination
        />
      </Box>
      <Box sx={underwritingTabStyles.buttonContainer}>
        <Button
          variant='text'
          color='secondary'
          disabled={isAddDisabled}
          onClick={() => {
            setSelectedActivity(null);
            setEditOpen(true);
          }}
          startIcon={<Plus color='primary' style={{ height: 16, width: 16 }} />}
        >
          Add Activity
        </Button>
      </Box>

      <EditUWActivityDialog
        quoteId={quoteId}
        editOpen={editOpen}
        setEditOpen={setEditOpen}
        selectedActivity={selectedActivity}
      />
    </Box>
  );
};
